import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import {
  Tag,
  Percent,
  DollarSign,
  Users,
  PiggyBank,
  Plus,
  Loader2,
  AlertCircle,
  X,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Eye,
  CheckCircle,
  Clock
} from 'lucide-react';

interface DiscountCode {
  id: string;
  coupon_id: string;
  code: string;
  name: string | null;
  discount_type: 'percent_off' | 'amount_off';
  discount_value: number;
  currency: string;
  duration: 'once' | 'repeating' | 'forever';
  duration_in_months: number | null;
  max_redemptions: number | null;
  times_redeemed: number;
  expires_at: string | null;
  active: number;
  applies_to_plans: string;
  first_time_transaction: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  local_redemptions?: number;
  total_savings?: number;
}

interface Redemption {
  id: string;
  discount_code_id: string;
  user_id: string;
  stripe_subscription_id: string | null;
  amount_saved: number;
  redeemed_at: string;
  studio_name: string | null;
  user_email: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface Stats {
  totalCodes: number;
  activeCodes: number;
  totalRedemptions: number;
  totalSavings: number;
}

const durationLabels: Record<string, string> = {
  once: 'One-time',
  repeating: 'Repeating',
  forever: 'Forever',
};

export default function DiscountsPage() {
  const { getToken } = useAuth();
  const [discounts, setDiscounts] = useState<DiscountCode[]>([]);
  const [stats, setStats] = useState<Stats>({ totalCodes: 0, activeCodes: 0, totalRedemptions: 0, totalSavings: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRedemptionsModal, setShowRedemptionsModal] = useState(false);
  const [selectedDiscount, setSelectedDiscount] = useState<DiscountCode | null>(null);
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    discountType: 'percent_off' as 'percent_off' | 'amount_off',
    discountValue: '',
    duration: 'once' as 'once' | 'repeating' | 'forever',
    durationInMonths: '',
    maxRedemptions: '',
    expiresAt: '',
    firstTimeTransaction: false,
  });

  const fetchDiscounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/discounts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error('Failed to fetch discounts');
      }
      const data = await response.json();
      setDiscounts(data.discounts || []);
      setStats(data.stats || { totalCodes: 0, activeCodes: 0, totalRedemptions: 0, totalSavings: 0 });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load discounts');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchDiscounts();
  }, [fetchDiscounts]);

  const handleCreateDiscount = async () => {
    if (!formData.code.trim() || !formData.discountValue) {
      setError('Code and discount value are required');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/discounts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: formData.code.toUpperCase(),
          name: formData.name || formData.code.toUpperCase(),
          discountType: formData.discountType,
          discountValue: formData.discountType === 'percent_off'
            ? parseFloat(formData.discountValue)
            : parseFloat(formData.discountValue) * 100, // Convert dollars to cents for amount_off
          duration: formData.duration,
          durationInMonths: formData.duration === 'repeating' ? parseInt(formData.durationInMonths) || undefined : undefined,
          maxRedemptions: formData.maxRedemptions ? parseInt(formData.maxRedemptions) : undefined,
          expiresAt: formData.expiresAt || undefined,
          firstTimeTransaction: formData.firstTimeTransaction,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create discount');
      }

      setShowCreateModal(false);
      setFormData({
        code: '',
        name: '',
        discountType: 'percent_off',
        discountValue: '',
        duration: 'once',
        durationInMonths: '',
        maxRedemptions: '',
        expiresAt: '',
        firstTimeTransaction: false,
      });
      fetchDiscounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create discount');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (discount: DiscountCode) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/discounts/${discount.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active: !discount.active }),
      });

      if (!response.ok) {
        throw new Error('Failed to update discount');
      }

      fetchDiscounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update discount');
    }
  };

  const handleDelete = async (discount: DiscountCode) => {
    if (!confirm(`Are you sure you want to delete the discount code "${discount.code}"?`)) {
      return;
    }

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/discounts/${discount.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete discount');
      }

      fetchDiscounts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete discount');
    }
  };

  const handleViewRedemptions = async (discount: DiscountCode) => {
    setSelectedDiscount(discount);
    setShowRedemptionsModal(true);

    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.serenetouch.app';
      const token = await getToken();
      const response = await fetch(`${apiUrl}/admin/discounts/${discount.id}/redemptions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch redemptions');
      }

      const data = await response.json();
      setRedemptions(data.redemptions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch redemptions');
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isExpired = (expiresAt: string | null) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <div className="space-y-6">
      {/* Error Banner */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-medium text-red-700 dark:text-red-300">Error</p>
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/40 rounded">
            <X className="w-4 h-4 text-red-500" />
          </button>
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 dark:bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-stone-900 rounded-xl p-6 flex items-center gap-3 shadow-xl">
            <Loader2 className="w-6 h-6 text-sage animate-spin" />
            <span className="text-stone-700 dark:text-stone-300">Loading discounts...</span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-stone-800 dark:text-stone-100">Discounts</h1>
          <p className="text-stone-500 dark:text-stone-400 mt-1">
            Manage discount codes for early adopters and promotions
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Create Discount
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-sage/10 rounded-lg flex items-center justify-center">
              <Tag className="w-5 h-5 text-sage" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{stats.totalCodes}</p>
              <p className="text-sm text-stone-500">Total Codes</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{stats.activeCodes}</p>
              <p className="text-sm text-stone-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">{stats.totalRedemptions}</p>
              <p className="text-sm text-stone-500">Redemptions</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-center justify-center">
              <PiggyBank className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-stone-800 dark:text-stone-100">${(stats.totalSavings / 100).toFixed(0)}</p>
              <p className="text-sm text-stone-500">Total Savings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Discounts List */}
      <div className="bg-white dark:bg-stone-900 rounded-xl border border-stone-200 dark:border-stone-800 overflow-hidden">
        {discounts.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-stone-100 dark:bg-stone-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Tag className="w-8 h-8 text-stone-400" />
            </div>
            <h3 className="text-lg font-semibold text-stone-800 dark:text-stone-100 mb-2">No discount codes yet</h3>
            <p className="text-stone-500 dark:text-stone-400 mb-4">Create your first discount code for early adopters</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium hover:bg-sage/90"
            >
              <Plus className="w-4 h-4" />
              Create Discount
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-stone-200 dark:border-stone-800">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Discount</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Duration</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Redemptions</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-stone-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((discount) => (
                  <tr key={discount.id} className="border-b border-stone-100 dark:border-stone-800 hover:bg-stone-50 dark:hover:bg-stone-800/50">
                    <td className="px-4 py-4">
                      <div>
                        <p className="font-mono font-bold text-stone-800 dark:text-stone-100">{discount.code}</p>
                        {discount.name && discount.name !== discount.code && (
                          <p className="text-sm text-stone-500">{discount.name}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        {discount.discount_type === 'percent_off' ? (
                          <>
                            <Percent className="w-4 h-4 text-sage" />
                            <span className="font-semibold text-stone-800 dark:text-stone-100">{discount.discount_value}% off</span>
                          </>
                        ) : (
                          <>
                            <DollarSign className="w-4 h-4 text-sage" />
                            <span className="font-semibold text-stone-800 dark:text-stone-100">${(discount.discount_value / 100).toFixed(0)} off</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-stone-600 dark:text-stone-400">
                        {durationLabels[discount.duration]}
                        {discount.duration === 'repeating' && discount.duration_in_months && (
                          <span className="text-stone-500"> ({discount.duration_in_months} mo)</span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        onClick={() => handleViewRedemptions(discount)}
                        className="text-stone-600 dark:text-stone-400 hover:text-sage"
                      >
                        {discount.times_redeemed}
                        {discount.max_redemptions && (
                          <span className="text-stone-400"> / {discount.max_redemptions}</span>
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      {isExpired(discount.expires_at) ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 rounded-lg text-sm">
                          <Clock className="w-3 h-3" />
                          Expired
                        </span>
                      ) : discount.active ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg text-sm">
                          <CheckCircle className="w-3 h-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400 rounded-lg text-sm">
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleViewRedemptions(discount)}
                          className="p-2 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                          title="View redemptions"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleToggleActive(discount)}
                          className="p-2 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg text-stone-500 hover:text-stone-700 dark:hover:text-stone-300"
                          title={discount.active ? 'Deactivate' : 'Activate'}
                        >
                          {discount.active ? (
                            <ToggleRight className="w-4 h-4 text-green-500" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDelete(discount)}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-stone-500 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Discount Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
              <h2 className="text-xl font-bold text-stone-800 dark:text-stone-100">Create Discount Code</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* Code */}
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Code *
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                  placeholder="EARLYBIRD50"
                  className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl font-mono uppercase"
                />
                <p className="text-xs text-stone-500 mt-1">Letters, numbers, and underscores only</p>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Name (optional)
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Early Adopter 50% Off"
                  className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl"
                />
              </div>

              {/* Discount Type & Value */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                    Type
                  </label>
                  <select
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value as 'percent_off' | 'amount_off' })}
                    className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl"
                  >
                    <option value="percent_off">Percentage</option>
                    <option value="amount_off">Fixed Amount</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                    Value *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={formData.discountValue}
                      onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                      placeholder={formData.discountType === 'percent_off' ? '50' : '25'}
                      className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl pr-10"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-500">
                      {formData.discountType === 'percent_off' ? '%' : '$'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                    Duration
                  </label>
                  <select
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value as 'once' | 'repeating' | 'forever' })}
                    className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl"
                  >
                    <option value="once">One-time (first invoice)</option>
                    <option value="repeating">Repeating (X months)</option>
                    <option value="forever">Forever</option>
                  </select>
                </div>
                {formData.duration === 'repeating' && (
                  <div>
                    <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                      Months
                    </label>
                    <input
                      type="number"
                      value={formData.durationInMonths}
                      onChange={(e) => setFormData({ ...formData, durationInMonths: e.target.value })}
                      placeholder="3"
                      className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl"
                    />
                  </div>
                )}
              </div>

              {/* Max Redemptions */}
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Max Redemptions (optional)
                </label>
                <input
                  type="number"
                  value={formData.maxRedemptions}
                  onChange={(e) => setFormData({ ...formData, maxRedemptions: e.target.value })}
                  placeholder="100"
                  className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl"
                />
                <p className="text-xs text-stone-500 mt-1">Leave empty for unlimited</p>
              </div>

              {/* Expiration */}
              <div>
                <label className="block text-sm font-medium text-stone-700 dark:text-stone-300 mb-1">
                  Expiration Date (optional)
                </label>
                <input
                  type="date"
                  value={formData.expiresAt}
                  onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                  className="w-full px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl"
                />
              </div>

              {/* First-time only */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.firstTimeTransaction}
                  onChange={(e) => setFormData({ ...formData, firstTimeTransaction: e.target.checked })}
                  className="w-5 h-5 rounded border-stone-300 text-sage focus:ring-sage"
                />
                <span className="text-stone-700 dark:text-stone-300">
                  First-time customers only
                </span>
              </label>
            </div>
            <div className="p-6 border-t border-stone-200 dark:border-stone-800 flex justify-end gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDiscount}
                disabled={saving}
                className="px-6 py-2 bg-sage text-white rounded-xl font-medium hover:bg-sage/90 disabled:opacity-50 flex items-center gap-2"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redemptions Modal */}
      {showRedemptionsModal && selectedDiscount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-stone-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-stone-800 dark:text-stone-100">Redemptions</h2>
                <p className="text-stone-500 font-mono">{selectedDiscount.code}</p>
              </div>
              <button
                onClick={() => {
                  setShowRedemptionsModal(false);
                  setSelectedDiscount(null);
                  setRedemptions([]);
                }}
                className="p-2 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-lg"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            <div className="p-6">
              {redemptions.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">No redemptions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {redemptions.map((redemption) => (
                    <div
                      key={redemption.id}
                      className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-800 rounded-xl"
                    >
                      <div>
                        <p className="font-medium text-stone-800 dark:text-stone-100">
                          {redemption.studio_name || 'Unknown Studio'}
                        </p>
                        <p className="text-sm text-stone-500">
                          {redemption.first_name} {redemption.last_name} ({redemption.user_email})
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-stone-500">{formatDate(redemption.redeemed_at)}</p>
                        {redemption.amount_saved > 0 && (
                          <p className="text-sm text-green-600">Saved ${(redemption.amount_saved / 100).toFixed(2)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
